import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DownloadIcon from '@mui/icons-material/Download';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormHelperText,
  IconButton,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Tooltip,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createTenant, generateTenantProvisioningScript, getTenant, updateTenant } from '../../api/endpoints';
import { queryKeys } from '../../api/queryKeys';
import type {
  ProvisioningScriptKind,
  TenantDetail,
  TenantProvisioningScript,
  TenantSummary,
} from '../../api/types';
import { ErrorBlock, LoadingBlock } from '../../components/QueryStateOverlay';
import { useErrorToast, useSuccessToast } from '../../hooks/useErrorToast';
import {
  derivedContainerName,
  derivedQueueInName,
  derivedQueueOutName,
  isValidTenantCode,
  normalizeTenantCode,
} from './tenantCode';

export interface TenantDialogProps {
  /** Null creates a tenant; otherwise the registration is replaced wholesale. */
  tenant: TenantSummary | TenantDetail | null;
  onClose: () => void;
}

function isDetail(tenant: TenantSummary | TenantDetail): tenant is TenantDetail {
  return 'containerName' in tenant;
}

/**
 * The API reports the EFFECTIVE names, not the stored overrides. An effective value equal to what
 * the code derives is indistinguishable from no override — and equivalent: saved back as empty it
 * keeps following the code, which is also the behaviour a rename should have.
 */
function overrideOf(effective: string, derived: string): string {
  return effective === derived ? '' : effective;
}

/**
 * Drops the file banner (the block between the two `# ====` lines) for the clipboard: the copy is
 * headed for a console, where the banner is only scroll. Section comments stay, and the preview
 * and the download keep the banner — a saved file should still say what it is.
 */
export function stripFileBanner(script: string): string {
  return script.replace(/^# =+\r?\n(?:#.*\r?\n)*# =+\r?\n\s*\n/, '');
}

/** Hands the generated script to the browser as a .ps1 download. */
function downloadScript(fileName: string, script: string) {
  const url = URL.createObjectURL(new Blob([script], { type: 'text/plain;charset=utf-8' }));
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.click();
  URL.revokeObjectURL(url);
}

export function TenantDialog({ tenant, onClose }: TenantDialogProps) {
  // The update is wholesale and the summary row lacks the bus names, so editing from the list
  // first reads the tenant — prefilling from less would quietly clear every override on save.
  const needsDetail = tenant !== null && !isDetail(tenant);
  const editedGid = tenant?.tenantGid ?? '';

  const detailQuery = useQuery({
    queryKey: queryKeys.admin.tenant(editedGid),
    queryFn: ({ signal }) => getTenant(editedGid, signal),
    enabled: needsDetail,
  });

  const source = tenant === null ? null : isDetail(tenant) ? tenant : detailQuery.data;

  if (needsDetail && !detailQuery.isSuccess) {
    return (
      <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
        <DialogContent>
          {detailQuery.isError ? (
            <ErrorBlock error={detailQuery.error} onRetry={() => void detailQuery.refetch()} />
          ) : (
            <LoadingBlock />
          )}
        </DialogContent>
      </Dialog>
    );
  }

  return <TenantForm tenant={source ?? null} onClose={onClose} />;
}

function TenantForm({ tenant, onClose }: { tenant: TenantDetail | null; onClose: () => void }) {
  const queryClient = useQueryClient();
  const toastError = useErrorToast();
  const toastSuccess = useSuccessToast();
  const { t } = useTranslation();

  const [name, setName] = useState(() => tenant?.name ?? '');
  const [code, setCode] = useState(() => tenant?.code ?? '');
  const [containerName, setContainerName] = useState(() =>
    tenant ? overrideOf(tenant.containerName, derivedContainerName(tenant.code)) : '',
  );
  const [queueInName, setQueueInName] = useState(() =>
    tenant ? overrideOf(tenant.queueInName, derivedQueueInName(tenant.code)) : '',
  );
  const [queueOutName, setQueueOutName] = useState(() =>
    tenant ? overrideOf(tenant.queueOutName, derivedQueueOutName(tenant.code)) : '',
  );
  const [useQueues, setUseQueues] = useState(() => tenant?.useQueues ?? false);
  const [testMode, setTestMode] = useState(() => tenant?.testMode ?? false);
  const [enabled, setEnabled] = useState(() => tenant?.enabled ?? true);

  const normalizedCode = normalizeTenantCode(code);
  const codeInvalid = normalizedCode !== '' && !isValidTenantCode(normalizedCode);
  // Placeholders follow the code as it is typed, so the operator sees the names a save produces.
  const placeholderCode = isValidTenantCode(normalizedCode) ? normalizedCode : '{code}';

  const mutation = useMutation({
    mutationFn: () => {
      const body = {
        name: name.trim(),
        code: normalizedCode,
        enabled,
        containerName: containerName.trim() || null,
        queueInName: queueInName.trim() || null,
        queueOutName: queueOutName.trim() || null,
        useQueues,
        testMode,
      };
      return tenant ? updateTenant(tenant.tenantGid, body) : createTenant(body);
    },
    onSuccess: async (saved) => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.admin.all });
      await queryClient.invalidateQueries({ queryKey: queryKeys.caller.all });
      toastSuccess(t('admin.tenants.dialog.saved', { name: saved.name }));
      onClose();
    },
    onError: toastError,
  });

  // Provisioning scripts are built from the form as it stands, not from the saved tenant — that is
  // what lets an administrator take the create script away before ever pressing Save.
  const [scriptMenuAnchor, setScriptMenuAnchor] = useState<HTMLElement | null>(null);
  const scriptButtonRef = useRef<HTMLButtonElement | null>(null);
  // Shown in a dialog to review and copy — the typical destination is a terminal or VS Code, not a
  // file; downloading stays available as the secondary action.
  const [scriptPreview, setScriptPreview] = useState<TenantProvisioningScript | null>(null);

  const scriptMutation = useMutation({
    mutationFn: (kind: ProvisioningScriptKind) =>
      generateTenantProvisioningScript({
        name: name.trim(),
        code: normalizedCode,
        kind,
        containerName: containerName.trim() || null,
        queueInName: queueInName.trim() || null,
        queueOutName: queueOutName.trim() || null,
        useQueues,
      }),
    onSuccess: setScriptPreview,
    onError: toastError,
  });

  const pickScript = (kind: ProvisioningScriptKind) => {
    setScriptMenuAnchor(null);
    scriptMutation.mutate(kind);
  };

  const copyScript = async (script: string) => {
    try {
      await navigator.clipboard.writeText(stripFileBanner(script));
      toastSuccess(t('actions.copied'));
    } catch (error) {
      toastError(error);
    }
  };

  return (
    <Dialog open onClose={mutation.isPending ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{tenant ? t('admin.tenants.dialog.editTitle') : t('admin.tenants.register')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ mt: 1 }}>
          <TextField
            label={t('columns.name')}
            value={name}
            onChange={(event) => setName(event.target.value)}
            required
          />
          <TextField
            label={t('admin.tenants.code')}
            value={code}
            onChange={(event) => setCode(event.target.value)}
            required
            error={codeInvalid}
            helperText={
              codeInvalid ? t('admin.tenants.dialog.codeInvalid') : t('admin.tenants.dialog.codeHelp')
            }
            slotProps={{ htmlInput: { style: { fontFamily: 'monospace' } } }}
          />
          {/* The labels stay shrunk so the derived-name placeholder is visible without focusing —
              it is the answer to "what will this be called if I leave it empty". */}
          <TextField
            label={t('admin.tenants.dialog.containerName')}
            value={containerName}
            onChange={(event) => setContainerName(event.target.value)}
            placeholder={derivedContainerName(placeholderCode)}
            helperText={t('admin.tenants.dialog.overrideHelp')}
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <TextField
            label={t('admin.tenants.dialog.queueInName')}
            value={queueInName}
            onChange={(event) => setQueueInName(event.target.value)}
            placeholder={derivedQueueInName(placeholderCode)}
            helperText={t('admin.tenants.dialog.overrideHelp')}
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <TextField
            label={t('admin.tenants.dialog.queueOutName')}
            value={queueOutName}
            onChange={(event) => setQueueOutName(event.target.value)}
            placeholder={derivedQueueOutName(placeholderCode)}
            helperText={t('admin.tenants.dialog.overrideHelp')}
            slotProps={{ inputLabel: { shrink: true } }}
          />
          {/* One row, two equal columns: the switches align, and each carries its explanation. */}
          <Stack direction="row" spacing={3}>
            <Box sx={{ flex: 1 }}>
              <FormControlLabel
                control={<Switch checked={enabled} onChange={(event) => setEnabled(event.target.checked)} />}
                label={t('states.enabled')}
              />
              <FormHelperText>{t('admin.tenants.dialog.enabledHelp')}</FormHelperText>
            </Box>
            <Box sx={{ flex: 1 }}>
              <FormControlLabel
                control={
                  <Switch checked={useQueues} onChange={(event) => setUseQueues(event.target.checked)} />
                }
                label={t('admin.tenants.dialog.useQueues')}
              />
              <FormHelperText>{t('admin.tenants.dialog.useQueuesHelp')}</FormHelperText>
            </Box>
          </Stack>
          <Box>
            <FormControlLabel
              control={<Switch checked={testMode} onChange={(event) => setTestMode(event.target.checked)} />}
              label={t('admin.tenants.dialog.testMode')}
            />
            <FormHelperText>{t('admin.tenants.dialog.testModeHelp')}</FormHelperText>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        {/* Bottom-left, away from Cancel/Save: not part of saving. Generates the Azure provisioning
            (or read-only check) script from the form as shown; the administrator runs it under
            their own az login. */}
        <Button
          ref={scriptButtonRef}
          onClick={() => setScriptMenuAnchor(scriptButtonRef.current)}
          disabled={scriptMutation.isPending || name.trim() === '' || normalizedCode === '' || codeInvalid}
          startIcon={scriptMutation.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}
          endIcon={<ArrowDropDownIcon />}
          sx={{ mr: 'auto' }}
        >
          {t('admin.tenants.dialog.generateScript')}
        </Button>
        <Menu
          anchorEl={scriptMenuAnchor}
          open={scriptMenuAnchor !== null}
          onClose={() => setScriptMenuAnchor(null)}
        >
          <MenuItem onClick={() => pickScript('Create')}>
            <ListItemText
              primary={t('admin.tenants.dialog.scriptCreate')}
              secondary={t('admin.tenants.dialog.scriptCreateHelp')}
            />
          </MenuItem>
          <MenuItem onClick={() => pickScript('Check')}>
            <ListItemText
              primary={t('admin.tenants.dialog.scriptCheck')}
              secondary={t('admin.tenants.dialog.scriptCheckHelp')}
            />
          </MenuItem>
        </Menu>
        <Button onClick={onClose} disabled={mutation.isPending}>
          {t('actions.cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={() => mutation.mutate()}
          disabled={mutation.isPending || name.trim() === '' || normalizedCode === '' || codeInvalid}
          startIcon={mutation.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {mutation.isPending && !tenant ? t('admin.tenants.dialog.provisioning') : t('actions.save')}
        </Button>
      </DialogActions>

      {scriptPreview && (
        <Dialog open onClose={() => setScriptPreview(null)} maxWidth="md" fullWidth>
          {/* The actions live in the header next to the file name — the dialog is one block of
              text, so the eye is already there and the footer would only add height. */}
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box component="span" sx={{ fontFamily: 'monospace', flexGrow: 1, minWidth: 0 }}>
              {scriptPreview.fileName}
            </Box>
            <Tooltip title={t('actions.download')}>
              <IconButton onClick={() => downloadScript(scriptPreview.fileName, scriptPreview.script)}>
                <DownloadIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title={t('actions.copyToClipboard')}>
              <IconButton onClick={() => void copyScript(scriptPreview.script)}>
                <ContentCopyIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title={t('actions.close')}>
              <IconButton onClick={() => setScriptPreview(null)}>
                <CloseIcon />
              </IconButton>
            </Tooltip>
          </DialogTitle>
          <DialogContent>
            <Box
              component="pre"
              sx={{
                m: 0,
                p: 2,
                maxHeight: '60vh',
                overflow: 'auto',
                bgcolor: 'action.hover',
                borderRadius: 1,
                fontFamily: 'monospace',
                fontSize: 12,
                lineHeight: 1.5,
              }}
            >
              {scriptPreview.script}
            </Box>
          </DialogContent>
        </Dialog>
      )}
    </Dialog>
  );
}
